/**
 * ENHANCED TERRAIN — the decoration layer, and the only part of this lane's
 * work behind a setting.
 *
 * The rolling ground itself (`packages/engines flight/terrain-relief.ts`) is not here: it feeds
 * the sim's `GroundQuery` and so has to be identical for every peer, replay and
 * census. Everything in *this* module is something nothing can collide with, so
 * a player can switch it off and get today's pixels back exactly.
 *
 * Three things, in descending order of how well the data supports them. That
 * ordering is the honest part and it is worth stating plainly rather than
 * burying: only the first is anchored to a real object.
 *
 * ### 1. Copses — anchored to shipped placements, and standing *in place of* them
 *
 * The game places one tree object 19-40 times per region — a polygon trunk
 * carrying three type-1 SPHERE nodes, which its renderer draws as flat filled
 * discs (AD-019 §2.2; `tree-geometry.ts` for the measurements). Each placement
 * becomes a copse of instanced trees at the same position, inside the same
 * crown envelope, in the same four `BARON.PAL` entries — and the copse's first
 * tree stands exactly on the original's trunk.
 *
 * With the toggle on, the discs those trees replace are **hidden**
 * (`setCanopyDiscsVisible`, wired from `flight/scene-terrain.ts`), because
 * drawing both is drawing the same tree twice. With the toggle off nothing
 * here runs and the discs are the only vegetation, exactly as before.
 *
 * **What is measured and what is invented.** Measured: the placement
 * positions, the crown radius and centre height, the four palette entries, the
 * object's 47 x 20 m extent. Invented: *the shape* — lobe and skirt
 * arrangement, trunk taper, facet counts, which surface is lit; the nine trees
 * a copse is made of and the 23 m they are scattered over; and everything in
 * §2 below. An earlier version of this header claimed "nothing is invented but
 * the shape", which was never true of §2 and is not true of the copse count
 * either. AD-019 §5.6 called that out; this is the correction.
 *
 * ### 2. Woods — derived from landcover, by an interpretation, and wholly invented as objects
 *
 * Each region carries one large landcover patchwork object — Verdun's
 * `TERR5-72` is 43 polygons over 42 x 40 km in nine palette entries, London's
 * `TERR1-71` is 89 polygons in sixteen. Those entries split cleanly into browns
 * (128, 130, 137-142 — ploughed earth) and greens (132 `#616d10`, 133
 * `#516534`, 134 `#556134`, 143 `#4d5928`).
 *
 * **Reading the green entries as woodland and pasture is an interpretation, not
 * a decode.** The data says "this polygon is this colour"; it does not say what
 * grows there. What makes the reading defensible is that the shipped tree
 * object's own canopies are painted **133 and 134** — two of those same green
 * entries — so the game itself paints its trees the colour it paints that
 * ground. Woods are therefore scattered only inside polygons whose colour the
 * game already uses for foliage, never on the ploughed browns.
 *
 * That is where the evidence stops. **A wood is a port invention end to end**:
 * the original places no tree anywhere except its 19-40 shipped copses, so
 * every one of the up-to-400 sites of 26 trees here is vegetation the game
 * never had. The landcover colour picks *where* it is plausible to put one; it
 * does not say one is there. Only the tree's own dimensions are measured.
 *
 * ### 3. Water and field tone — designed, NOT BUILT
 *
 * Two further treatments belong under this toggle and are deliberately absent
 * rather than half-done, because both turned out to be more than the
 * vertex-colour rewrites they look like:
 *
 * - **Moving water.** `WATER_RAMP` (`world/terrain-palette.ts`) already
 *   recovers the game's own blue run 1-7 with the measured river/sea colour at
 *   its centre, so a swell quantised through it would stay in palette. What
 *   stops it is plumbing, not colour: water faces live inside the same shared
 *   geometry as every other face of their object, so animating them means
 *   splitting them into their own mesh with its own node material. That is the
 *   first thing to build next, and it is what the Channel needs.
 * - **Field parcels.** The landcover plates are *subdivided* already, by
 *   `terrain-drape.ts`, so the mesh to carry per-parcel tone exists — but
 *   choosing tones is a design question (how much variation before a quilt
 *   stops reading as farmland) that wants a picture to argue over first.
 *
 * ## Budget, and why it is tiles now and not four meshes
 *
 * This used to be four `InstancedMesh`es, one per variant, each covering the
 * whole region — and that is a trap disguised as a good number. three.js culls
 * per *object*, so a mesh whose bounding sphere spans 42 km is never culled:
 * every instance in the region was submitted every frame no matter where the
 * camera pointed, and the vertex shader threw most of them away after
 * transforming them. "Four draw calls" was true and beside the point.
 *
 * Instances are therefore bucketed by variant **and by a `TILE_M` grid square**.
 * Each mesh gets a tight bounding sphere, so the frustum test does real work: a
 * forward view holds a fraction of the region's tiles and the rest never reach
 * the GPU. The price is draw calls in the low tens instead of four, which is
 * the cheaper side of that trade on every GPU this port can run on — and it is
 * what buys the headroom for `tree-geometry.ts`'s richer variants (47 triangles
 * a tree, up from 32).
 *
 * Culling on a tile boundary cannot pop: three.js drops a mesh only when its
 * bounding sphere is wholly outside the frustum, so nothing visible is ever
 * removed.
 *
 * All materials opaque, depth buffer untouched, no `renderOrder`, no alpha —
 * lessons.md 2026-08-19 and 2026-08-22.
 */

import * as THREE from "three/webgpu";
import { createTreeVariants, type TreeVariant } from "./tree-geometry.ts";
import type { GroundSampler } from "./terrain-drape.ts";

/**
 * Side of a bucketing tile, metres. Regions are 42 x 40 km, so this is a 6 x 5
 * grid at most — few enough objects that the per-object cull loop is free, big
 * enough that a tile holds a useful batch instead of a dozen instances.
 */
export const TILE_M = 8192;

/** Trees in a copse placed by the original — it draws three crowns, so a copse is a small stand. */
export const COPSE_TREES = 9;
/** Radius a copse's trees are scattered over, metres — the shipped object's own 47 m extent, halved. */
export const COPSE_RADIUS_M = 23;

/**
 * One wood per this many square metres of green landcover.
 *
 * Chosen against a *coverage fraction*, not picked. A wood here is a
 * `WOOD_RADIUS_M` disc — 0.053 km² — so one per 0.35 km² puts roughly **15% of
 * the green landcover under trees**, which is the low end of what French
 * farmland actually is. The first pass used one per 2.2 km², i.e. 2.4%, and the
 * result was a feature nobody could find: a low pass over Verdun crossed no
 * wood at all, and 28 frames of census could not tell the toggle's two states
 * apart.
 */
export const WOOD_AREA_PER_SITE_M2 = 3.5e5;
/** Trees in a derived wood, and the radius they cover. */
export const WOOD_TREES = 26;
export const WOOD_RADIUS_M = 130;
/**
 * Ceiling on derived woods per region, so a huge patchwork cannot run away with
 * the frame budget. At 400 sites Verdun draws ~10 000 trees — still four draw
 * calls, because the cost of a site is instances rather than a batch.
 */
export const MAX_WOOD_SITES = 400;

/** Green landcover entries — the ones the game itself paints foliage with. See the module doc. */
export const FOLIAGE_GROUND_COLOURS: readonly number[] = [0x616d10, 0x516534, 0x556134, 0x4d5928];

/** Deterministic integer hash → [0,1). */
function hash01(a: number, b: number): number {
  let h = Math.imul(a, 0x27d4eb2d) ^ Math.imul(b, 0x165667b1);
  h = Math.imul(h ^ (h >>> 15), 0x2c1b3c6d);
  h ^= h >>> 13;
  return (h >>> 0) / 4294967296;
}

export interface TreeSite {
  readonly x: number;
  readonly z: number;
  readonly count: number;
  readonly radiusM: number;
}

/**
 * A variant-and-tile bucket key. Tile indices can be negative, so they are
 * stringified rather than packed.
 *
 * The tile is chosen from the **site's** centre, not from each tree's own
 * position. Bucketing per tree splits every site that happens to straddle a
 * grid line across up to four tiles — a site on the origin produced fourteen
 * near-empty meshes instead of four. A site is at most `WOOD_RADIUS_M` across,
 * so keeping it whole overhangs its tile by 130 m out of 8 192 and costs the
 * cull nothing; the bounding spheres are computed from the instances anyway.
 */
function bucketKey(variant: number, siteX: number, siteZ: number): string {
  return `${variant}|${Math.floor(siteX / TILE_M)}|${Math.floor(siteZ / TILE_M)}`;
}

/**
 * Scatters `site.count` trees over a disc, deterministically from the site's
 * own position, into per-variant-per-tile buckets.
 *
 * Tree 0 sits exactly on the site centre and is not scattered. For a copse
 * that centre is the original object's own trunk, so the enhanced tree stands
 * where the game's tree stands rather than beside it — which is what makes
 * hiding the disc a replacement instead of a deletion.
 */
function scatterSite(
  site: TreeSite,
  index: number,
  sample: GroundSampler,
  out: Map<string, THREE.Matrix4[]>,
  variants: number,
): void {
  const matrix = new THREE.Matrix4();
  const scaleVector = new THREE.Vector3();
  for (let i = 0; i < site.count; i++) {
    const seed = index * 977 + i;
    // sqrt keeps the disc evenly covered instead of clumping at the middle.
    const r = i === 0 ? 0 : Math.sqrt(hash01(seed, 1)) * site.radiusM;
    const a = hash01(seed, 2) * Math.PI * 2;
    const x = site.x + Math.cos(a) * r;
    const z = site.z + Math.sin(a) * r;
    const scale = 0.75 + hash01(seed, 3) * 0.6;
    matrix.makeRotationY(hash01(seed, 4) * Math.PI * 2);
    matrix.scale(scaleVector.set(scale, scale, scale));
    matrix.setPosition(x, sample(x, z), z);
    const key = bucketKey(Math.floor(hash01(seed, 5) * variants) % variants, site.x, site.z);
    const bucket = out.get(key);
    if (bucket) bucket.push(matrix.clone());
    else out.set(key, [matrix.clone()]);
  }
}

/** A landcover triangle, world space, already draped — what wood sites are derived from. */
export interface FoliagePatch {
  readonly ax: number;
  readonly az: number;
  readonly bx: number;
  readonly bz: number;
  readonly cx: number;
  readonly cz: number;
}

/** Wood sites inside the green landcover polygons, by area, capped. */
export function woodSitesFrom(patches: readonly FoliagePatch[]): TreeSite[] {
  const sites: TreeSite[] = [];
  patches.forEach((p, pi) => {
    const area = Math.abs((p.bx - p.ax) * (p.cz - p.az) - (p.cx - p.ax) * (p.bz - p.az)) / 2;
    const want = Math.floor(area / WOOD_AREA_PER_SITE_M2);
    for (let i = 0; i < want && sites.length < MAX_WOOD_SITES; i++) {
      // Uniform barycentric sample, so sites land inside the real polygon.
      let u = hash01(pi * 31 + i, 7);
      let v = hash01(pi * 31 + i, 11);
      if (u + v > 1) {
        u = 1 - u;
        v = 1 - v;
      }
      sites.push({
        x: p.ax + u * (p.bx - p.ax) + v * (p.cx - p.ax),
        z: p.az + u * (p.bz - p.az) + v * (p.cz - p.az),
        count: WOOD_TREES,
        radiusM: WOOD_RADIUS_M,
      });
    }
  });
  return sites;
}

export interface TerrainDecoration {
  readonly group: THREE.Group;
  /** Meshes built. Every one is frustum-culled on its own tile, so the *drawn* count is lower in flight. */
  readonly drawCallCount: number;
  /** Triangles across every instance, if none were culled — the worst case, and the one worth pinning. */
  readonly triangleCount: number;
  readonly treeCount: number;
  readonly copseCount: number;
  readonly woodCount: number;
  readonly dispose: () => void;
}

/**
 * Builds every tree in the region as one `InstancedMesh` per variant per
 * populated tile. Callers add or remove `group` to switch the decoration;
 * nothing here is rebuilt on a toggle, which is what makes flipping it free.
 */
export function buildTerrainDecoration(
  copseSites: readonly TreeSite[],
  woodSites: readonly TreeSite[],
  sample: GroundSampler,
  /** The hour's walk down the canopy run — `flight/time-of-day.ts`'s `groundRampShiftFor`. */
  rampShift = 0,
): TerrainDecoration {
  const variants: TreeVariant[] = createTreeVariants(rampShift);
  const buckets = new Map<string, THREE.Matrix4[]>();

  copseSites.forEach((site, i) => scatterSite(site, i, sample, buckets, variants.length));
  woodSites.forEach((site, i) => scatterSite(site, i + 100_000, sample, buckets, variants.length));

  const group = new THREE.Group();
  const material = new THREE.MeshBasicMaterial({ vertexColors: true });
  const meshes: THREE.InstancedMesh[] = [];
  let treeCount = 0;
  let triangleCount = 0;

  // Sorted, so a region's meshes are added in the same order on every run —
  // `Map` iteration follows insertion, and insertion here follows a hash.
  for (const key of [...buckets.keys()].sort()) {
    const matrices = buckets.get(key)!;
    const variant = variants[Number(key.slice(0, key.indexOf("|")))]!;
    const mesh = new THREE.InstancedMesh(variant.geometry, material, matrices.length);
    matrices.forEach((m, i) => mesh.setMatrixAt(i, m));
    mesh.instanceMatrix.needsUpdate = true;
    // The tight per-tile sphere this yields is the entire point of bucketing.
    mesh.computeBoundingSphere();
    group.add(mesh);
    meshes.push(mesh);
    treeCount += matrices.length;
    triangleCount += matrices.length * variant.triangleCount;
  }

  return {
    group,
    drawCallCount: meshes.length,
    triangleCount,
    treeCount,
    copseCount: copseSites.length,
    woodCount: woodSites.length,
    dispose: () => {
      // The four variant geometries are *shared* across tiles, so they are
      // disposed once from the variant list — walking `meshes` would dispose
      // each of them a tileful of times.
      for (const variant of variants) variant.geometry.dispose();
      // One shared material across every variant — disposed once, and disposing
      // it is what frees the render objects (lessons.md 2026-08-19, rule 5).
      material.dispose();
    },
  };
}
